<template>
    <!-- Update Payment Method Modal -->
    <Modal v-model="opened" width="800px" :title="modalTitle">
        <template #content>
            <CreditCardForm
                :noMargin="true"
                :noTitle="true"
                :updating-card="!!creditCard"
                @on-success="onSuccess"
            />
        </template>
    </Modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { Modal, showSnackbar } from '@opteo/components-next'

import CreditCardForm from '@/components/billing/CreditCardForm.vue'

import { loadStripe } from '@stripe/stripe-js'
import { useSubscription } from '@/composition/billing/useSubscription'
import { Endpoint, authRequest } from '@/composition/api/useAPI'
import { STRIPE_PUBLISHABLE_KEY } from '@/lib/env'
import { Api } from '@opteo/types'

const props = defineProps<{
    modelValue: boolean
}>()

const emit = defineEmits(['update:modelValue'])

const modalTitle = computed(() =>
    creditCard.value ? 'Update Payment Method' : 'Add Payment Method'
)

const opened = ref(false)

const { mutate, creditCard } = useSubscription()

watch(props, async ({ modelValue }) => {
    opened.value = modelValue
})

watch(opened, async newVal => {
    emit('update:modelValue', newVal)
})

async function onSuccess(paymentMethod: string) {
    const result = await authRequest<Api.UpdateCustomerCc.Response>(Endpoint.UpdateCustomerCc, {
        body: { token: paymentMethod },
    })

    // 3DS validation
    if (result.status === 'requires_action') {
        const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY)
        if (!stripe) throw new Error('Stripe failed to load')
        const { error } = await stripe.confirmCardPayment(result.client_secret, {
            payment_method: paymentMethod,
        })
        if (error) throw new Error(error.message)
    }

    await mutate()

    // For the animation but also lets swrv revalidate
    opened.value = false

    // The card was saved, but collecting the outstanding overdue invoice failed
    // (e.g. the new card was declined). Surface it — the invoice is still unpaid.
    if (result.status === 'collection_failed') {
        showSnackbar({
            message: `Card saved, but we couldn't collect your outstanding payment. Please try another card or contact support.`,
            timeout: 6000,
        })
    }
}
</script>
